import { useState } from "react";
import moment from "moment";
import BackIcon from "assets/icons/back.svg?react";
import ArrowLeftIcon from "assets/icons/arrow_l.svg?react";
import Input from "components/Input";
import HollowButton from "components/HollowButton";
import Spinner from "components/Spinner";
import ConfigureCru from "components/ConfigureCru";
import cruService from "api/cruService";
import { ENotificationType, notification } from "utils/notification";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";

// Strona ustawień integracji CRU (Integracje → karta „Centralny Rejestr Umów" → Edytuj) —
// port grupy CRU z Kwantum.Administration/Settings/View/View.xaml: Status / Ważny od /
// Ważny do + „Usuń klucz" / „Aktualizuj klucz" (edytor nowego klucza pod spodem).
// Układ jak KSeFSettings (pasek ścieżki z powrotem, pola tylko do odczytu).

interface Props {
  onCanceled: () => void;
  onSaved: () => void;
  /** Rozłączenie = usunięcie klucza (obsługiwane tutaj, nie przez IntegrationsTab.handleDisconnect). */
  onDisconnected: () => void;
}

const CruSettings = ({ onCanceled, onSaved, onDisconnected }: Props) => {
  const { t } = useTranslation();
  const { data: settings, isLoading } = cruService.useGetSettings();
  const { mutate: removeApiKey, isLoading: removing } = cruService.useRemoveApiKey();
  const [editingKey, setEditingKey] = useState(false);

  if (isLoading || !settings) {
    return <Spinner />;
  }

  const statusText = !settings.HasApiKey
    ? "Brak klucza API"
    : settings.IsActive
    ? "Aktywna"
    : "Nieaktywna (klucz wygasł)";

  const fmt = (iso?: string | null) => (iso ? moment(iso).format("YYYY-MM-DD") : "—");

  return (
    <div className={styles.wrapper}>
      <div className={styles.pathBar}>
        <div className={styles.backButton}>
          <BackIcon onClick={onCanceled} />
        </div>
        {t("integrations.title")} <ArrowLeftIcon /> Centralny Rejestr Umów (CRU)
      </div>

      <Input label="Status" value={statusText} onChange={() => {}} disabled />
      <Input label="Ważny od" value={fmt(settings.KeyCreationDate)} onChange={() => {}} disabled />
      <Input label="Ważny do" value={fmt(settings.KeyValidTo)} onChange={() => {}} disabled />
      <Input
        label="REGON jednostki"
        value={settings.InstitutionRegon ?? "—"}
        onChange={() => {}}
        disabled
      />
      <Input
        label="Środowisko"
        value={settings.Environment === "Production" ? "produkcyjne" : "testowe MF"}
        onChange={() => {}}
        disabled
      />
      <div className={styles.hint}>
        Klucz API wydaje Ministerstwo Finansów; jest ważny 6 miesięcy − 1 dzień od wydania.
        Publikacji w CRU podlegają umowy zawarte od{" "}
        {moment(settings.PublicationStart).format("DD.MM.YYYY")}.
      </div>

      {!editingKey && (
        <div className={styles.actions}>
          <HollowButton
            text="Usuń klucz"
            disabled={removing || !settings.HasApiKey}
            onClick={() =>
              removeApiKey(undefined, {
                onSuccess: () => {
                  notification({
                    type: ENotificationType.SUCCESS,
                    title: "Centralny Rejestr Umów",
                    text: "Klucz API CRU został usunięty.",
                  });
                  onDisconnected();
                },
              })
            }
          />
          <HollowButton text="Aktualizuj klucz" onClick={() => setEditingKey(true)} />
        </div>
      )}

      {editingKey && (
        <ConfigureCru
          isUpdate
          initialRegon={settings.InstitutionRegon}
          toggle={() => setEditingKey(false)}
          onSuccess={() => {
            setEditingKey(false);
            onSaved();
          }}
        />
      )}
    </div>
  );
};

export default CruSettings;
